import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NotificationsService } from '@app/components/notification/NotificationsService';
import { Question } from '@app/forms/subscription/definition/interface';
import { environment } from '@environments/environment';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as _ from 'lodash';
import { Observable, catchError, finalize, mergeMap, tap } from 'rxjs';
import { PledgeRead } from '../../definition/interface/Pledge';
import { SubscriptionFacadeService } from '../../services/subscription-facade.service';
import { getCommands } from '../../services/utile';
import { completedQuestions, steps } from '../utils';

interface CategorizedData {
  [key: string]: {
    data: Question[];
    previousStepName: string;
    previous?: boolean;
    isComplete: boolean;
    previousStepCompleted: boolean;
    allPreviousStepsCompleted?: boolean;
  };
}
@UntilDestroy()
@Component({
  selector: 'ea-subscription-form-stepper',
  templateUrl: './ea-subscription-form-stepper.component.html',
  styleUrls: ['./ea-subscription-form-stepper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EaSubscriptionFormStepper implements OnInit, AfterViewInit {
  @ViewChildren('selectStep') selectStep: QueryList<ElementRef>;
  @Input() isConsultant = true;
  pictureFile: string = '';
  consultantBackUrl = environment.servers.resources.url || '';
  toggle: boolean = false;
  stepIndex;
  question: Question;
  questions: Question[] = [];
  step;
  questionsByCategory;

  steps = steps;
  completedQuestions = completedQuestions;
  stepIconCompleted = {
    ...this.completedQuestions,
  };
  category: string;
  pledge: PledgeRead;
  productType;
  isProgress = false;
  isAutoCompliteCategoris = [];
  totalQuestions;
  currentQuestion;
  stepChanged$: Observable<any>;
  isSubmitting: boolean = false;
  groupedData;
  categorizedData: CategorizedData = {};

  constructor(
    public subscriptionFacade: SubscriptionFacadeService,
    private route: ActivatedRoute,
    private cd: ChangeDetectorRef,
    private notificationService: NotificationsService,
  ) {}
  ngAfterViewInit(): void {}

  ngOnInit(): void {
    this.productType = this.route.snapshot.paramMap.get('id');
    this.subscribeToProgressChanges();
    this.subscribeToStepChanges();
    this.listenClickSubject();
  }
  private subscribeToProgressChanges(): void {
    this.subscriptionFacade.iisProgressChanged$
      .pipe(untilDestroyed(this))
      .subscribe(isProgress => {
        this.isProgress = isProgress;
      });
  }
  private subscribeToStepChanges(): void {
    this.stepChanged$ = this.subscriptionFacade.stepChanged$.pipe(
      untilDestroyed(this),
      mergeMap(step => {
        this.step = step;
        return this.subscriptionFacade.questionsChanged$;
      }),
      tap(questions => {
        if (questions && this.step) {
          this.initializeQuestions();

          this.groupDataByCategory();
        }
      }),
    );
  }
  private initializeQuestions() {
    this.questions = this.subscriptionFacade.getQuestions();
    this.steps = [...new Set(this.questions.map(q => q.category))];

    this.question = this.questions[this.step - 1];
    this.category = this.question?.category;
    if (this.category) {
      this.questionsByCategory = this.questions.filter(
        question => question.category === this.category,
      );
    }
    /**
     * remove setIsAutoCompleteCategory
     */
    this.setIsAutoCompleteCategory();
    this.setStepIndex(this.question);

    /**
     * remove this.totalQuestions and this.currentQuestion
     */
    this.totalQuestions = this.getQuestionTotal(this.category);

    this.currentQuestion = this.findIndexQuestionCategory(
      this.questions,
      this.question,
    );
  }

  private getQuestionTitle(question: Question): string {
    return !_.isNil(question.getTitle)
      ? question.getTitle(
          this.subscriptionFacade.getPledge(),
          this.subscriptionFacade.getUser(),
        )
      : question.title;
  }

  private groupDataByCategory(): void {
    this.groupedData = _.groupBy(this.questions, 'category');
    _.forEach(this.groupedData, questions =>
      _.remove(
        questions,
        (question: Question) => !this.getQuestionTitle(question),
      ),
    );
    if (!this.steps?.length) {
      return;
    }
    this.steps?.forEach((category, index) => {
      const isCurrentCategoryComplete = this.isComplete(category);
      const allPreviousStepsCompleted =
        this.areAllPreviousStepsCompleted(index);

      this.categorizedData[category] = {
        data: this.groupedData[category],
        previousStepName: this.getPreviousStepName(index),
        isComplete: isCurrentCategoryComplete,
        previous: this.steps.indexOf(this.category) >= index,
        previousStepCompleted: this.isPreviousStepCompleted(index),
        allPreviousStepsCompleted,
      };
    });
  }
  private areAllPreviousStepsCompleted(index: number): boolean {
    if (index === 0) {
      return true;
    }

    return this.steps
      .slice(0, index)
      .every(prevCategory => this.categorizedData[prevCategory]?.isComplete);
  }
  private getPreviousStepName(index: number): string | null {
    return index === 0 ? null : this.steps[index - 1];
  }
  private isPreviousStepCompleted(index: number): boolean {
    return index === 0
      ? true
      : this.categorizedData?.[this?.steps?.[index - 1]]?.isComplete || false;
  }
  private listenClickSubject() {
    this.subscriptionFacade.clickSubject$
      .pipe(untilDestroyed(this))
      .subscribe(e => {
        this.setStepToInValidQuestion();
      });
  }

  private setStepIndex(question: Question) {
    if (this.category) {
      const questionsByCategory = this.questions.filter(
        question => question.category === this.category,
      );

      this.stepIndex = this.getIndexOfIdInNestedArray(
        this.chunkArray(questionsByCategory, 4),
        question.id,
      );
    }
  }

  private setIsAutoCompleteCategory() {
    if (!this.isProgress) {
      const categories = this.questions
        ?.filter(q => this.steps.includes(q.category))
        .map(q => q.category);

      this.isAutoCompliteCategoris = [...new Set(categories)];
    }
  }

  isComplete(category) {
    const pledge = this.subscriptionFacade.getPledge();
    const questionsByCategory = this.questions.filter(
      q => q.category === category,
      //&& !q.alwaysSkip
    );
    const questionsByCategoryAndCompleted = questionsByCategory.filter(
      q =>
        (q.callbackValidation &&
          q.callbackValidation(pledge, this.subscriptionFacade.getUser())) ||
        !q.callbackValidation,
    );

    const lengthCompletes = questionsByCategory.length;
    const lengthCompletesByCategory = questionsByCategoryAndCompleted.length;

    return lengthCompletes && lengthCompletesByCategory == lengthCompletes;
  }
  chunkArray(arr, n) {
    const chunkLength = Math.max(arr.length / n, 1);
    const chunks = [];
    for (let i = 0; i < n; i++) {
      if (chunkLength * (i + 1) <= arr.length)
        chunks.push(arr.slice(chunkLength * i, chunkLength * (i + 1)));
    }
    return chunks;
  }
  getIndexOfIdInNestedArray(nestedArray, id) {
    for (let i = 0; i < nestedArray.length; i++) {
      let index = nestedArray[i].findIndex(k => k.id == id);
      if (index > -1) {
        return i + 1;
      }
    }
  }
  getQuestionByCategory(questions, question) {
    return questions?.filter(q => q.category === question?.category);
  }
  findIndexQuestionCategory(questions = [], question) {
    const qs = this.getQuestionByCategory(questions, question);
    return qs?.findIndex(q => q?.id === question?.id);
  }
  getQuestionTotal(category) {
    return this.questions?.filter(q => q.category === category)?.length;
  }

  setStep(step, i) {
    const sortedObj = this.categorizedData;
    const allPrevStepsCompleted = sortedObj[step]?.allPreviousStepsCompleted;
    if (!allPrevStepsCompleted) return;

    if (step === 'Signature') this.subscriptionFacade.setCommand(null);

    this.pledge = this.subscriptionFacade.getPledge();
    const pledgeDocs = this.pledge.documents?.length;

    if (step === 'Signature' && !pledgeDocs) {
      this.updateCommand(step);
    } else {
      this.goToStep(step);
    }
  }

  private updateCommand(step: any) {
    const pledge = this.subscriptionFacade.getPledge();

    this.isSubmitting = true;
    this.subscriptionFacade.loading();
    this.subscriptionFacade
      .putPledge({
        ...pledge,
        commands: getCommands(),
      })
      .pipe(
        untilDestroyed(this),
        catchError(err => {
          let message = err?.error?.message || 'Une erreur est survenue';
          this.isSubmitting = false;
          this.subscriptionFacade.loaded();
          this.notificationService.error('La sauvegarde a échoué', message);
          throw err;
        }),
        finalize(() => {
          this.isSubmitting = false;
          this.subscriptionFacade.loaded();
          this.cd.detectChanges();
          this.subscriptionFacade.setCommand(null);
        }),
      )
      .subscribe(() => {
        this.isSubmitting = false;
        this.subscriptionFacade.loaded();
        this.goToStep(step);
      });
  }

  private goToStep(step: any) {
    this.subscriptionFacade.setIsProgress(true);

    const q = this.questions?.findIndex(q => q.category === step);

    if ((q || q == 0) && q !== -1) {
      this.subscriptionFacade.setStep(Number(q) + 1);
    }
  }

  nextKey(currentKey) {
    const completedQuestionsKeys = Object.keys(this.completedQuestions);
    const currentIndex = completedQuestionsKeys.indexOf(currentKey);

    if (currentIndex === -1) {
      return null;
    }

    const nextKey = completedQuestionsKeys[currentIndex + 1] || null;

    return nextKey;
  }

  next(currentIndex) {
    const nextQuestionKey = this.steps[currentIndex + 1];
    if (!nextQuestionKey) {
      return false;
    }

    return this.completedQuestions[nextQuestionKey];
  }

  getQuestionNotValidTitle(category) {
    const pledge = this.subscriptionFacade.getPledge();
    const result = this.questions
      ?.filter(
        q =>
          q.category == category &&
          this.getQuestionTitle(q) &&
          q?.callbackValidation &&
          !q?.callbackValidation(pledge, this.subscriptionFacade.getUser()),
      )
      .map(q => {
        return {
          ...q,
        };
      });

    return result;
  }

  setQuestionStep(question) {
    const q = this.questions?.findIndex(
      q => this.getQuestionTitle(q) === this.getQuestionTitle(question),
    );

    if ((q || q == 0) && q !== -1) {
      this.subscriptionFacade.setStep(Number(q) + 1);
    }
  }
  allStepValidExceptLast() {
    return this.steps
      .filter(s => s !== 'Signature')
      .every(step => this.categorizedData[step]?.isComplete);
  }

  setStepToInValidQuestion() {
    const pledge = this.subscriptionFacade.getPledge();
    const questions = this.questions;

    const invalidQuestion = questions.find(
      question =>
        question.callbackValidation &&
        !question.callbackValidation(pledge, this.subscriptionFacade.getUser()),
    );

    const invalidQuestionId = invalidQuestion?.id;
    let invalidQuestionIndex = questions?.findIndex(
      question => question?.id == invalidQuestionId,
    );

    if (invalidQuestionIndex == -1) {
      const allStepsValidExceptLast = this.allStepValidExceptLast();

      if (allStepsValidExceptLast) {
        if (!pledge?.documents?.length) {
          this.updateCommand('Signature');
        } else {
          this.goToStep('Signature');
        }
        return;
      }
    }

    if (invalidQuestionIndex != -1 && invalidQuestionIndex >= this.step) {
      this.subscriptionFacade.setStep(invalidQuestionIndex + 1);
    }
  }

  questionNotValid(q: Question) {
    const pledge = this.subscriptionFacade.getPledge();

    return (
      q?.callbackValidation &&
      !q?.callbackValidation(pledge, this.subscriptionFacade.getUser())
    );
  }
}
